import { useState } from "react"

import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import {
  useQueryAdminDashboardBehaviors,
  useQueryAdminDashboardBehaviorSeries,
} from "api/reactQuery/queries/adminDashboard"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  IDashboardBehaviorChartLine,
  IDashboardBehaviorSeriesPoint,
  IDashboardPeriodParams,
} from "ts/interfaces/AdminDashboard"
import { DashboardBehaviorType } from "ts/types/AdminDashboard"

import BehaviorChart from "./BehaviorChart"
import BehaviorFocusCard from "./BehaviorFocusCard"
import {
  BEHAVIOR_TYPE,
  behaviorLabel,
  hasMoreThan,
  isSameBehavior,
  RAW_INTERPOLATION,
  toBehaviorSlug,
} from "./behaviorFormat"
import BehaviorList from "./BehaviorList"
import BehaviorsFullView from "./BehaviorsFullView"
import BehaviorsSeeAllDialog from "./BehaviorsSeeAllDialog"
import DashboardSection from "./DashboardSection"
import DashboardSectionBody, {
  RowsSkeleton,
  useSectionState,
  useShownValue,
} from "./DashboardSectionBody"
import useBehaviorSelection from "./useBehaviorSelection"
import useBehaviorSelectionFocus from "./useBehaviorSelectionFocus"

const INLINE_ROWS = 10
const INLINE_PAGE = { skip: 0, limit: INLINE_ROWS }

const listsSx = {
  display: "grid",
  gridTemplateColumns: { mobile: "minmax(0, 1fr)", tablet: "1fr 1fr" },
  gap: 3,
}

const sumOfCategory = (
  points: IDashboardBehaviorSeriesPoint[] | undefined,
  type: DashboardBehaviorType,
) =>
  points?.reduce(
    (sum, point) => sum + point[BEHAVIOR_TYPE[type].countField],
    0,
  ) ?? null

const BonusesVsFines = ({ params }: { params: IDashboardPeriodParams }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { selection, select, clear } = useBehaviorSelection()
  // Without a selection both share a key, so only one request goes out.
  const chart = useQueryAdminDashboardBehaviorSeries(params, selection)
  const totals = useQueryAdminDashboardBehaviorSeries(params, null)
  // The chart's points may still be the previous selection's.
  const shown = useShownValue(selection, chart.isPreviousData)
  const bonus = useQueryAdminDashboardBehaviors(params, "bonus", INLINE_PAGE)
  const fine = useQueryAdminDashboardBehaviors(params, "fine", INLINE_PAGE)

  const bonusRows = bonus.data?.data.objects ?? []
  const fineRows = fine.data?.data.objects ?? []
  const hasMore = [bonus, fine].some((query) =>
    hasMoreThan(
      query.data?.data.all_objects_count,
      query.data?.data.objects.length ?? 0,
      INLINE_ROWS,
    ),
  )
  const isEmpty = !bonusRows.length && !fineRows.length
  // The chart query stays out, so a failed behaviour series can't replace
  // the lists; the chart area shows its own states.
  const sectionQueries = [totals, bonus, fine]
  const { state } = useSectionState(sectionQueries, isEmpty)
  // A period change already dims the whole section, so the chart doesn't
  // dim again on top of it.
  const isChartUpdating =
    chart.isPreviousData && chart.isFetching && state !== "updating"
  // A failed background refetch keeps the last good line on screen.
  const isChartError = chart.isError && chart.data === undefined
  const [seeAllOpen, setSeeAllOpen] = useState(false)
  const [fullViewType, setFullViewType] =
    useState<DashboardBehaviorType | null>(null)
  const {
    titleRef,
    chartAreaRef,
    clearButtonRef,
    selectedRowRef,
    announcement,
    handleClear,
    handleSelect,
    handleDialogSelect,
    handleDialogsExited,
  } = useBehaviorSelectionFocus({
    selection,
    select,
    clear,
    closeDialogs: () => {
      setSeeAllOpen(false)
      setFullViewType(null)
    },
  })

  const lineColor = {
    bonus: theme.palette.success.main,
    fine: theme.palette.error.main,
  }
  const behaviourLabel = shown && behaviorLabel(t, shown)
  const chartLines: IDashboardBehaviorChartLine[] = shown
    ? [{ key: shown.type, color: lineColor[shown.type], name: shown.title }]
    : [
        {
          key: "bonus",
          color: lineColor.bonus,
          name: t("adminDashboard.bonuses"),
        },
        { key: "fine", color: lineColor.fine, name: t("adminDashboard.fines") },
      ]

  // Count and share describe the chart's points, so they wait until those are
  // the requested behaviour's in the requested period.
  const count =
    selection && !chart.isPreviousData
      ? sumOfCategory(chart.data?.data.points, selection.type)
      : null
  // The totals may still be the previous period's.
  const categoryTotal =
    selection && !totals.isPreviousData
      ? sumOfCategory(totals.data?.data.points, selection.type)
      : null
  const share =
    count !== null && categoryTotal
      ? Math.round((count / categoryTotal) * 100)
      : null

  const trendRow =
    selection &&
    { bonus: bonusRows, fine: fineRows }[selection.type].find((row) =>
      isSameBehavior(selection, { type: selection.type, title: row.title }),
    )

  return (
    <DashboardSection
      title={t("adminDashboard.bonusesVsFines")}
      titleRef={titleRef}
      question={t("adminDashboard.bonusesVsFinesQuestion")}
      exportImage={{
        section: shown
          ? `bonuses-vs-fines_${shown.type}_${toBehaviorSlug(shown.title)}`
          : "bonuses-vs-fines",
        detail: behaviourLabel || undefined,
        dateFrom: chart.data?.data.date_from,
        dateTo: chart.data?.data.date_to,
        // The chart must show the requested line before it can be exported.
        disabled:
          state !== "ready" ||
          chart.isLoading ||
          isChartError ||
          chart.isPreviousData,
      }}
    >
      <Box role="status" sx={visuallyHiddenSx}>
        {announcement}
      </Box>
      <DashboardSectionBody
        queries={sectionQueries}
        // Both lists empty replaces the whole section, so a flat all-zero
        // chart is never drawn.
        isEmpty={isEmpty}
        emptyText={t("adminDashboard.bonusesVsFinesEmpty")}
        skeleton={
          <Stack rowGap={3}>
            <Skeleton variant="rounded" height={200} />
            <Box sx={listsSx}>
              <RowsSkeleton rows={INLINE_ROWS} />
              <RowsSkeleton rows={INLINE_ROWS} />
            </Box>
          </Stack>
        }
      >
        <Stack rowGap={4}>
          <Stack
            ref={chartAreaRef}
            rowGap={2}
            data-testid="behavior-chart-area"
          >
            {selection && (
              <BehaviorFocusCard
                selection={selection}
                onClear={handleClear}
                clearRef={clearButtonRef}
                count={count}
                share={share}
                trendRow={trendRow ?? undefined}
              />
            )}
            {chart.isLoading ? (
              <Skeleton
                variant="rounded"
                height={260}
                aria-busy="true"
                data-testid="chart-skeleton"
              />
            ) : isChartError ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                rowGap={1}
                sx={{ minHeight: 260, textAlign: "center" }}
              >
                <Typography variant="body2" color="mockup.neutral30">
                  {t("adminDashboard.chartError")}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => chart.refetch()}
                >
                  {t("adminDashboard.tryAgain")}
                </Button>
              </Stack>
            ) : (
              <Stack rowGap={1}>
                {selection && count === 0 && (
                  <Typography variant="body2" color="mockup.neutral30">
                    {t(BEHAVIOR_TYPE[selection.type].noneInPeriodKey, {
                      ...RAW_INTERPOLATION,
                      title: selection.title,
                    })}
                  </Typography>
                )}
                {isChartUpdating && (
                  <Typography variant="body2" color="mockup.neutral30">
                    {t("adminDashboard.chartUpdating")}
                  </Typography>
                )}
                <Box
                  data-testid="behavior-chart"
                  sx={{
                    opacity: isChartUpdating ? 0.4 : 1,
                    transition: "opacity 150ms",
                  }}
                >
                  <BehaviorChart
                    points={chart.data?.data.points ?? []}
                    lines={chartLines}
                    tableLabel={
                      behaviourLabel
                        ? t("adminDashboard.behaviourTable", {
                            ...RAW_INTERPOLATION,
                            behaviour: behaviourLabel,
                          })
                        : t("adminDashboard.bonusesVsFinesTable")
                    }
                  />
                </Box>
              </Stack>
            )}
          </Stack>
          <Box sx={listsSx}>
            <BehaviorList
              type="bonus"
              rows={bonusRows}
              selection={selection}
              onSelect={handleSelect}
              selectedRef={selectedRowRef}
            />
            <BehaviorList
              type="fine"
              rows={fineRows}
              selection={selection}
              onSelect={handleSelect}
              selectedRef={selectedRowRef}
            />
          </Box>
          {hasMore && (
            <Button
              variant="text"
              aria-haspopup="dialog"
              onClick={() => setSeeAllOpen(true)}
              data-export-ignore
              sx={{ alignSelf: "center" }}
            >
              {t("adminDashboard.seeAllBehaviours")}
            </Button>
          )}
          <BehaviorsSeeAllDialog
            params={params}
            open={seeAllOpen}
            onClose={() => setSeeAllOpen(false)}
            onSeeMore={setFullViewType}
            selection={selection}
            onSelect={handleDialogSelect}
            onExited={handleDialogsExited}
          />
          {/* Remounted per opening so it always starts on page 1 */}
          <BehaviorsFullView
            key={fullViewType === null ? "closed" : "open"}
            params={params}
            type={fullViewType}
            onTypeChange={setFullViewType}
            onClose={() => setFullViewType(null)}
            selection={selection}
            onSelect={handleDialogSelect}
          />
        </Stack>
      </DashboardSectionBody>
    </DashboardSection>
  )
}

export default BonusesVsFines
